import { z } from 'zod'
import { localDayAt, parseIsoInstant } from './time'

export const MOODS = [
  { value: 1, label: '很低落', color: '#e0def2' },
  { value: 2, label: '低落', color: '#d7e8f1' },
  { value: 3, label: '平静', color: '#dcebdc' },
  { value: 4, label: '开心', color: '#ffe8b6' },
  { value: 5, label: '很开心', color: '#ffd9cd' },
] as const
export type MoodValue = typeof MOODS[number]['value']

export function isLocalDay(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const instant = new Date(`${value}T12:00:00Z`)
  return Number.isFinite(instant.getTime()) && instant.toISOString().slice(0, 10) === value
}
export function shiftDay(day: string, offset: number): string {
  if (!isLocalDay(day)) throw new Error('请选择有效日期')
  const instant = new Date(`${day}T12:00:00Z`)
  instant.setUTCDate(instant.getUTCDate() + offset)
  return instant.toISOString().slice(0, 10)
}
const daySchema = z.string().refine(isLocalDay, '请选择有效日期')
const timeZoneSchema = z.string().refine((value) => {
  try { localDayAt('2026-01-01T00:00:00Z', value); return value.length > 0 } catch { return false }
}, '时区无效')
const instantSchema = z.string().refine((value) => {
  try { return parseIsoInstant(value).toISOString() === value } catch { return false }
})
const dayRecord = {
  localDay: daySchema,
  timeZone: timeZoneSchema,
  source: z.literal('manual'),
  createdAt: instantSchema,
  updatedAt: instantSchema,
}
export const moodRecordSchema = z.strictObject({
  ...dayRecord,
  mood: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
  note: z.string().refine((value) => [...value].length <= 200, '最多写200字').optional(),
})
export const periodRecordSchema = z.strictObject(dayRecord)
export type MoodRecord = z.infer<typeof moodRecordSchema>
export type PeriodRecord = z.infer<typeof periodRecordSchema>

export function validateJournalDay(day: string, timeZone: string, now: Date): void {
  daySchema.parse(day)
  timeZoneSchema.parse(timeZone)
  if (day > localDayAt(now.toISOString(), timeZone)) throw new Error('只能记录今天或过去的日期')
}
export function periodDays(start: string, end: string, timeZone: string, now: Date): string[] {
  validateJournalDay(start, timeZone, now)
  validateJournalDay(end, timeZone, now)
  if (start > end) throw new Error('结束日期不能早于开始日期')
  const length = Math.round((Date.parse(`${end}T12:00:00Z`) - Date.parse(`${start}T12:00:00Z`)) / 86400000) + 1
  if (length > 366) throw new Error('一次最多选择366天')
  return Array.from({ length }, (_, index) => shiftDay(start, index))
}

export function validateJournalSnapshot(snapshot: { moods?: MoodRecord[]; periods?: PeriodRecord[] }): void {
  for (const [records, schema] of [[snapshot.moods === undefined ? [] : snapshot.moods, moodRecordSchema], [snapshot.periods === undefined ? [] : snapshot.periods, periodRecordSchema]] as const) {
    if (!Array.isArray(records)) throw new Error('记录格式无效')
    const days = new Set<string>()
    for (const record of records) {
      schema.parse(record)
      if (days.has(record.localDay)) throw new Error('同一天只能有一条记录')
      days.add(record.localDay)
    }
  }
}
